/******************************************
 *  Author : vsuthar
 *  Created On : Thu Sep 02 2021
 *  File : RCTree.js
 *  ©Palo Alto Networks
 *******************************************/

// Libs
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
  useContext,
  VFC
} from 'react'
import {
  Button,
  Input,
  TruncateText,
  Tooltip,
  classNames,
  useModal, useToast,
} from '@panwds/react-ui'
import {
  CaretRightIcon,
  SearchIcon,
  RemoveIcon,
  CircleFilledIcon
} from '@panwds/icons'
import styled from 'styled-components'
import ContainerDimensions from 'react-container-dimensions'
import Tree from 'rc-tree'
import { isEqual } from 'lodash'
import type { Instance, Tsg } from '@sparky/framework'
import { Resizable } from 're-resizable'
// context
import GlobalContext from '../../../context/GlobalContext'
import useReadOnly from '../../../hooks/useReadOnly'

// reducers
import { setIsTenantMGMLoading } from '../../../reducers/actions/ViewReducer.actions'

// components
import Sidebar from '../sidebar/Sidebar'
import { TreeSearch, searchTenant } from '../sidebar/TreeSearch'
import { selectTreeNode, onExpandedKey } from '../../../reducers/actions/TreeReducer.actions'
import IconMenu, { IconMenuItem } from './IconMenu'
import MoveTenantModal from '../../../../tenancy/components/moveTenant/MoveTenantModal'
import AcquireTenantModal from '../../../../tenancy/components/acquireTenant/AcquireTenantModal'

// utils
import { isElement } from '../../../utils/DomUtils'
import { getVarValue } from '../../../utils/Sparky'

// constants
import {
  FF_ALLOW_ROOT_CREATE,
  FF_ENABLE_INSTANCE_LICENSE_EDIT,
  FF_ENHANCED_TSG_DELETE,
  FF_RC_TREE_V2,
  FF_TENANT_ACQUISITIONS
} from '../../../utils/FeatureFlags'
import {
  SEARCH_TENANTS
} from '../../../constants/LabelConstants'
import {
  ROOT_NODE_ID,
  TENANT_ADD,
  TENANT_DELETE,
  TENANT_EDIT,
  TENANT_MOVE,
  TENANT_ACQUIRE
} from '../../../../tenancy/constants/TenancyConstants'
import {
  APP_ID_PRISMA_ACCESS_PANORAMA,
  TENANCY_PERMISSION_SET
} from '../../../constants/GlobalConstants'
import {
  LABEL_ADD_NEW_TENANT,
  LABEL_DELETE_TENANT,
  LABEL_EDIT_TENANT,
  LABEL_MOVE_TENANT,
  LABEL_ACQUIRE_TENANT, LABEL_SUCCESSFUL_ACQUISITION_REQUESTED
} from '../../../../tenancy/constants/LabelConstants'

// styles
import './RCTree.scss'
import StringTemplate from '../../../utils/StringTemplate'
import { LoadAcqList } from '../../../actions/GlobalActions'
import { TSG_ACQUISITION_PERMISSION_SET } from '../../../../iam/constants/IAMConstants'
import { ARRAY } from '../../../utils/Utils'

// feature flags
const allowRootCreate = getVarValue(FF_ALLOW_ROOT_CREATE, false)
const allowNewTenant = getVarValue(FF_ENABLE_INSTANCE_LICENSE_EDIT, false)
const enhancedTSGDelete = getVarValue(FF_ENHANCED_TSG_DELETE, false)
const enableIconMenuForAll = getVarValue(FF_RC_TREE_V2, false)
const tsgMoveEnabled = getVarValue(FF_TENANT_ACQUISITIONS, false)

const TREE = styled(Tree as any)`
  visibility: ${props => (props.search ? 'hidden' : 'visible')};
  height: ${props => (props.search ? '0' : '')};
  overflow: ${props => (props.search ? 'hidden' : 'visible')};
`

/**
 * Tree component shared between Tenant Management and Identity&Access management
 * @param dispatch
 * @param treeProps
 * @param showContextButton
 * @param globalExpandedKeys
 * @param doDispatchGlobal
 * @returns {JSX.Element}
 * @constructor
 */
const RCTree: VFC<{
  treeProps: any,
  showContextButton: boolean,
  onContextClick?: (actionType: string, tenant: Tsg) => void,
  expandedKeys: string[],
  doDispatchGlobal: boolean,
  basic?: boolean
}> = ({
  treeProps,
  showContextButton = enableIconMenuForAll,
  onContextClick,
  expandedKeys: globalExpandedKeys,
  doDispatchGlobal = true,
  basic = false
}) => {

  const { licenseReducer = {}, treeReducer = {}, dispatch } = useContext(GlobalContext)
  const { updatedLicenseInfo = [] } = licenseReducer
  const { sgTree, flattenTree, selectedTreeNode, sgKeyBy, instancesKeyBy, expandedKeys: _expandedKeys } = treeReducer
  const { toast } = useToast()

  const readOnly = useReadOnly(selectedTreeNode?.id, TENANCY_PERMISSION_SET)
  const acqReadOnly = useReadOnly(selectedTreeNode?.id, TSG_ACQUISITION_PERMISSION_SET)

  // const addTSGModal = useModal(TenantCreationModal)
  // const deleteTSGModal = useModal(DeleteTSGModal)
  const moveTSGModal = useModal(MoveTenantModal)
  const acquireTSGModal = useModal(AcquireTenantModal)

  const [expandedKeys, setExpandedKeys] = useState<(string | null)[]>([])
  const [autoExpandParent, setAutoExpandParent] = useState(false)
  const [searchInput, setSearchInput] = useState('')

  const tsgRef = useRef<Tsg>()
  const sidebarRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const hasKeys = expandedKeys.some(Boolean)
    if (expandedKeys.length === 0 || !hasKeys) {
      setExpandedKeys(globalExpandedKeys)
    }
  }, [setExpandedKeys, expandedKeys, globalExpandedKeys])

  useEffect(() => {
    if (ARRAY.hasArray(_expandedKeys)) {
      setExpandedKeys(Array.from(new Set([..._expandedKeys, ...expandedKeys])))
      dispatch(onExpandedKey([]))
    }
  }, [_expandedKeys, dispatch, expandedKeys])

  const scrollToRow = useCallback((tsg_id) => {
    setTimeout(() => {
      const tree = sidebarRef.current
      if (tree && isElement(tree)) {
        const element = tree.querySelector(`[data-tsgid="${tsg_id}"]`)
        if (element && isElement(element)) {
          if ('scrollIntoViewIfNeeded' in element) {
            (element as any).scrollIntoViewIfNeeded(true)
          }
          else {
            element.scrollIntoView?.({ inline: 'start', block: 'center' })
          }
        }
      }
    }, 100)
  }, [])

  useEffect(() => {
    if (tsgRef?.current?.id !== selectedTreeNode?.id) {
      tsgRef.current = selectedTreeNode
      let keys = [ROOT_NODE_ID]
      const addKey: (tsg: Tsg, keys: string[]) => void = (tsg, keys) => {
        if (tsg?.parent_id) {
          keys.push(tsg?.parent_id)
          const parent = sgKeyBy?.[tsg?.parent_id]
          if (parent) {
            addKey(parent, keys)
          }
        }
      }

      if (expandedKeys.includes(selectedTreeNode?.id)) {
        keys.push(selectedTreeNode?.id)
      }
      if (selectedTreeNode) {
        addKey(selectedTreeNode, keys)
      }
      keys = [...new Set([...keys])]
      if (!isEqual(keys, expandedKeys)) {
        setExpandedKeys(keys)
      }
    }
  }, [expandedKeys, scrollToRow, selectedTreeNode, sgKeyBy])

  useLayoutEffect(() => {
    if (selectedTreeNode?.id) {
      scrollToRow(selectedTreeNode?.id)
    }
  }, [scrollToRow, selectedTreeNode?.id])

  // const onCreatedTsg = useCallback((tsg: Tsg) => LoadTreeDataAsync(dispatch)
  //   .then(() => actions.switchTsg(tsg.id))
  //   .then(() => dispatch(selectTreeNode(tsg))), [dispatch])

  // const onAddTenantClick = useCallback((tenant: Tsg) => {
  //   addTSGModal.openModal({ selectedTsg: tenant, onCreatedTsg })
  // }, [addTSGModal, onCreatedTsg])
  //
  // const onEditTenantClick = useCallback((tenant: Tsg) => {
  //   addTSGModal.openModal({ selectedTsg: tenant, isEdit: true, onCreatedTsg, sgKeyBy })
  // }, [addTSGModal, onCreatedTsg, sgKeyBy])
  //
  // const onDeleteTenantClick = useCallback((tenant: Tsg) => {
  //   const instances = instancesKeyBy?.[selectedTreeNode?.id] || []
  //   const modalProps = {
  //     tenant,
  //     claimInfo: {},
  //     instances
  //   }
  //   deleteTSGModal.openModal(modalProps as any)
  // }, [deleteTSGModal, instancesKeyBy, selectedTreeNode?.id])

  const onMoveTenantClick = useCallback((tenant: Tsg) => {
    moveTSGModal.openModal({ tsg: tenant } as any)
  }, [moveTSGModal])

  const onAcquireTenantClick = useCallback((tenant: Tsg) => {
    const onSuccess = async (acquisition: any) => {
      await LoadAcqList(selectedTreeNode?.id, dispatch)
      toast(StringTemplate(LABEL_SUCCESSFUL_ACQUISITION_REQUESTED, [acquisition.target_tsg_id]) as string, { appearance: 'success' })
    }
    acquireTSGModal.openModal({ tsg: tenant, onSuccess } as any)
  }, [acquireTSGModal, dispatch, selectedTreeNode?.id, toast])

  const iconMenuList: (tsg_id: string) => IconMenuItem[] = useCallback((tsg_id) => {
    const instances = instancesKeyBy?.[tsg_id]
    const disableAdd = (!allowNewTenant && instances?.some((each: Instance) => each.app_id === APP_ID_PRISMA_ACCESS_PANORAMA)) || readOnly
    const disableEdit = tsg_id === ROOT_NODE_ID || !selectedTreeNode || readOnly
    const canMove = !selectedTreeNode?.isRoot
    const options = [
      {
        label: LABEL_ADD_NEW_TENANT,
        value: TENANT_ADD,
        disabled: disableAdd,
      },
    ]
    options.push({
      label: LABEL_EDIT_TENANT,
      value: TENANT_EDIT,
      disabled: disableEdit,
    })
    if (!basic) {
      if (tsg_id !== ROOT_NODE_ID) {
        if (canMove && !readOnly) {
          tsgMoveEnabled && options.push({
            label: LABEL_MOVE_TENANT,
            value: TENANT_MOVE,
            disabled: false,
            onItemClick: onMoveTenantClick
          } as any)
        }
        if (!acqReadOnly) {
          tsgMoveEnabled && options.push({
            label: LABEL_ACQUIRE_TENANT,
            value: TENANT_ACQUIRE,
            disabled: false,
            onItemClick: onAcquireTenantClick
          } as any)
        }
      }
    }
    options.push({
      label: LABEL_DELETE_TENANT,
      value: TENANT_DELETE,
      addClassName: 'cm-text-red-700',
      disabled: tsg_id === ROOT_NODE_ID || !enhancedTSGDelete,
    } as any)
    return options
  }, [selectedTreeNode, instancesKeyBy, readOnly, acqReadOnly, onMoveTenantClick, onAcquireTenantClick, basic])

  const getSwitcherIcon = useCallback((arg) => {
    if (arg?.isLeaf) {
      return null
    }
    const className = classNames(arg?.expanded && 'cm-rotate-90')
    return <CaretRightIcon size='xxs' className={className} />
  }, [])

  const treeData = useMemo(() => {
    const hasLicenseInSubTree: (treeData: Tsg[]) => boolean = (treeData) => {
      return Array.isArray(treeData) && treeData.some(each => {
        if (updatedLicenseInfo.includes(each.tsg_id)) {
          return true
        }
        if (each.children?.length) {
          return hasLicenseInSubTree(each.children)
        }
        return false
      })
    }

    const getTitle = (each: Tsg) => {
      const hasButton = allowRootCreate || each.id !== ROOT_NODE_ID
      // console.log('HAS BUTTON', hasButton, showContextButton)
      const appearance = each.id !== ROOT_NODE_ID ? 'dark' : 'light'
      return <div className="tw-flex tw-items-center tw-flex-row tw-justify-between cm-pr-1">
        <div className='tw-flex tw-flex-row'>
          <TruncateText {...{ appearance }} style={{ maxWidth: '9rem' }}>
            <span className={each.id !== ROOT_NODE_ID ? '' : 'tw-text-secondary'}>{each.display_name}</span>
          </TruncateText>
          {hasLicenseInSubTree([each]) && <Tooltip label='License updates remaining'>
            <CircleFilledIcon className='tw-pr-1 tw-pb-1 tw-pt-1 cm-text-blue-600' size='md' />
          </Tooltip>
          }
        </div>
        {hasButton && showContextButton && <IconMenu onContextClick={onContextClick} iconMenuList={iconMenuList(each.id)} tenant={each} />}
      </div>
    }

    const getTreeData: (children: Tsg[]) => any = (children) => {
      return children?.map((each) => ({
        ...each,
        key: each.id,
        style: { width: 'auto', minWidth: '264px' },
        title: getTitle(each),
        className: 'cm-rounded',
        children: each.children ? getTreeData(each.children) : undefined,
        isLeaf: !each.children,
        'data-tsgid': each.id,
        'data-testid': `tsg-id:${each.id}`,
      })) || []
    }

    return getTreeData(sgTree)
  }, [iconMenuList, onContextClick, updatedLicenseInfo, sgTree, showContextButton])

  const onSelect = useCallback((selectedKeys, e) => {
    if (e?.node?.id !== ROOT_NODE_ID) {
      if (selectedTreeNode?.id !== e?.node?.id) {
        dispatch(setIsTenantMGMLoading(true))
      }
      dispatch(selectTreeNode(e))
    }
  }, [dispatch, selectedTreeNode])

  const getParentKey: (key: string, tree: Tsg[]) => string = useCallback((key, tree) => {
    let parentKey
    for (let i = 0; i < tree.length; i++) {
      const node = tree[i]
      if (node.children) {
        if (node.children.some((item) => item.key === key)) {
          parentKey = node.key
        }
        else if (getParentKey(key, node.children)) {
          parentKey = getParentKey(key, node.children)
        }
      }
    }
    return parentKey
  }, [])

  const searchResults = useMemo(() => {
    return !searchInput?.trim().length ? [] : searchTenant(searchInput, sgTree[0])
  }, [searchInput, sgTree])

  const getSearchInput = useMemo(() => {
    return (
      <Input
        addClassName='tw-w-full dark:cm-border-gray-900 dark:cm-border-solid'
        prefix={<SearchIcon size='sm' className='cm-text-gray-300' />}
        suffix={
          <Button
            aria-label='clear'
            appearance='tertiary-clear'
            onClick={() => setSearchInput('')}
            icon={<RemoveIcon size='sm' className='cm-text-gray-300' />}
            addClassName={searchInput ? '' : 'cm-hidden'}
          />
        }
        placeholder={SEARCH_TENANTS}
        value={searchInput}
        onChange={event => setSearchInput(event?.target?.value)}
      />
    )
  }, [searchInput])

  const onExpand = useCallback((expandedKeys) => {
    setExpandedKeys(expandedKeys)
    if (doDispatchGlobal) {
      dispatch(onExpandedKey(expandedKeys))
    }
  }, [setExpandedKeys, doDispatchGlobal, dispatch])

  const onResultClick = useCallback((tenant: Tsg) => {
    const expandedKeys = (flattenTree as Tsg[]).map(item => {
      if (item?.title?.indexOf(tenant?.display_name) > -1) {
        return getParentKey(item.key, sgTree)
      }
      return null
    }).filter((item, i, self) => item && self.indexOf(item) === i)
    setSearchInput('')
    onSelect([], { node: tenant })
    setExpandedKeys(expandedKeys)
    setAutoExpandParent(true)
    scrollToRow(tenant?.id)
  }, [flattenTree, scrollToRow, getParentKey, onSelect, sgTree])

  return (
    <Resizable
      minWidth={264}
      minHeight='100%'
      maxWidth='50vw'
      style={{
        display: 'flex'
      }}
      defaultSize={{
        width: 264,
        height: '100%'
      }}
      enable={{
        right: true
      }}
    >
      <Sidebar treeRef={sidebarRef} title={getSearchInput}>
        <ContainerDimensions>
          {({ height }) => <div style={{ width: 'min-content', height }} className='scroll-container'>
            <TREE
              onLoad={() => {
                scrollToRow(selectedTreeNode?.id)
              }}
              search={!!searchInput}
              prefixCls='tenant-tree'
              showIcon={false}
              selectedKeys={[selectedTreeNode?.id]}
              expandedKeys={expandedKeys}
              switcherIcon={getSwitcherIcon}
              defaultExpandAll={true}
              onSelect={onSelect}
              onExpand={onExpand}
              autoExpandParent={autoExpandParent}
              treeData={treeData}
              {...treeProps}
            />
          </div>}
        </ContainerDimensions>
        {!!searchInput && (
          <div className='tw-absolute tw-inset-0 tw-bg-white dark:tw-bg-gray-1050 tw-z-20'>
            <TreeSearch {...{ searchInput, searchResults, onResultClick }} />
          </div>
        )}
      </Sidebar>
    </Resizable>
  )
}

export default RCTree
